import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { AuthPageShell } from '../components/auth/AuthPageShell';
import { useAuthFormToast } from '../components/auth/useAuthFormToast';
import { showAuthErrorToast } from '../utils/authConfigurationErrors';
import { useAuthGoogleConfigToast } from '../hooks/useAuthGoogleConfigToast';
import {
  authDividerRowSx,
  authFieldSx,
  authLinkSx,
  authMutedLinkSx,
  authPrimaryButtonSx,
} from '../components/auth/authFormStyles';
import { deriveProfileComplete } from '../utils/profileCompletion';
import {
  getVerificationEmailFromResponse,
  responseRequiresVerification,
} from '../utils/pendingVerification';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, signInWithGoogle, setPendingProfileUser, setPendingVerificationEmail } = useAuth();

  const redirectPath = useMemo(() => {
    const raw = searchParams.get('redirect');
    if (!raw) return '/';
    try {
      const decoded = decodeURIComponent(raw);
      return decoded.startsWith('/') ? decoded : '/';
    } catch {
      return '/';
    }
  }, [searchParams]);

  const isInviteJoin = redirectPath.includes('/community/join/');
  const signupHref =
    redirectPath !== '/'
      ? `/signup?redirect=${encodeURIComponent(redirectPath)}`
      : '/signup';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { showError, ToastHost } = useAuthFormToast();
  useAuthGoogleConfigToast(showError);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await login(email, password);

      if (responseRequiresVerification(response)) {
        setPendingVerificationEmail(getVerificationEmailFromResponse(response, email));
        navigate('/verify-otp', {
          state: { email: email.trim() },
          replace: true,
        });
        return;
      }

      if (!response?.client) {
        const apiMessage =
          typeof response?.message === 'string' && response.message.trim()
            ? response.message
            : 'Login failed. Please check your credentials.';
        showAuthErrorToast(showError, apiMessage, 'Login failed. Please check your credentials.');
        return;
      }

      const profileComplete =
        response.client.hasCompletedProfile ?? deriveProfileComplete(response.client);
      if (!profileComplete) {
        navigate('/complete-profile', { replace: true });
      } else {
        navigate(redirectPath);
      }
    } catch (err: unknown) {
      showAuthErrorToast(showError, err, 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = useCallback(
    async (credential: string) => {
      setLoading(true);
      try {
        const response = await signInWithGoogle(credential);
        if (!response.hasCompletedProfile) {
          setPendingProfileUser({ isNewSocialUser: response.isNewSocialUser });
          navigate('/complete-profile');
        } else {
          navigate(redirectPath);
        }
      } catch (err: unknown) {
        showAuthErrorToast(showError, err, 'Google sign-in failed. Please try again.');
        setLoading(false);
      }
    },
    [signInWithGoogle, setPendingProfileUser, navigate, redirectPath, showError]
  );

  const handleGoogleError = useCallback(
    (err: Error) => {
      showAuthErrorToast(showError, err, 'Google sign-in failed.');
    },
    [showError]
  );

  return (
    <>
    <AuthPageShell
      title={isInviteJoin ? 'Log in to join' : 'Welcome back'}
      subtitle={
        isInviteJoin
          ? 'Sign in to accept this community invite.'
          : 'Sign in to your Canadawale Yar account'
      }
    >
      {isInviteJoin && (
        <Alert severity="info" sx={{ mb: 2, width: '100%', borderRadius: 2 }}>
          Don&apos;t have an account? Create one below after signing in.
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
        <TextField
          fullWidth
          label="Email"
          name="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          variant="outlined"
          autoComplete="email"
          autoFocus
          required
          sx={authFieldSx}
        />

        <TextField
          fullWidth
          label="Password"
          name="password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          variant="outlined"
          autoComplete="current-password"
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" aria-label="Toggle password">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ ...authFieldSx, mb: 1 }}
        />

        <Box sx={{ textAlign: 'right', mb: 2.5 }}>
          <Link component={RouterLink} to="/forgot-password" sx={authMutedLinkSx}>
            Forgot password?
          </Link>
        </Box>

        <Button type="submit" fullWidth variant="contained" disabled={loading} sx={authPrimaryButtonSx}>
          {loading ? 'Signing in…' : 'Sign in'}
        </Button>
      </Box>

      <Box sx={authDividerRowSx}>
        <Box sx={{ flex: 1, height: '1px', bgcolor: '#E8EAED' }} />
        <Typography variant="body2" sx={{ mx: 2, color: '#565959', fontSize: '0.8rem' }}>
          or continue with
        </Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: '#E8EAED' }} />
      </Box>

      <Box sx={{ width: '100%', mb: 2.5 }}>
        <GoogleSignInButton
          onSuccess={handleGoogleSignIn}
          onError={handleGoogleError}
          text="signin_with"
        />
      </Box>

      <Typography variant="body2" sx={{ color: '#565959', textAlign: 'center' }}>
        Don&apos;t have an account?{' '}
        <Link component={RouterLink} to={signupHref} sx={authLinkSx}>
          Create account
        </Link>
      </Typography>

      <Typography variant="caption" sx={{ mt: 2.5, color: '#565959', textAlign: 'center', display: 'block' }}>
        Need help?{' '}
        <Link href="mailto:support@canadawaleyar.com" sx={authMutedLinkSx}>
          support@canadawaleyar.com
        </Link>
      </Typography>
    </AuthPageShell>
    <ToastHost />
    </>
  );
};
